// src/context/DarkModeContext.tsx

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSettings } from './SettingsContext';
import { createLogger } from '../utils/logger';

// Enable more detailed logging for this component
const logger = createLogger({ module: 'DarkMode', level: 'info' });

type DarkModeContextType = {
  darkMode: boolean;
  toggleDarkMode: () => void;
};

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

// Add local storage key for dark mode fallback
const DARK_MODE_STORAGE_KEY = 'careercard_dark_mode';

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with local storage preference as first priority, then system preference
  const [darkMode, setDarkMode] = useState(() => {
    logger.debug('Initializing dark mode...');
    
    // First check local storage for a cached preference
    const storedPreference = localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (storedPreference !== null) {
      const isDark = storedPreference === 'true';
      logger.debug('Initial dark mode from localStorage:', { isDark });
      return isDark;
    }
    
    // Fall back to system preference
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    logger.debug('Initial dark mode from system preference:', { prefersDark });
    return prefersDark;
  });
  
  // Track whether we've initialized from settings yet
  const [initialized, setInitialized] = useState(false);
  
  // Track whether we're in the middle of a settings update
  const [updating, setUpdating] = useState(false);
  
  const { settings, updateSettings } = useSettings();
  
  // Use ref to track if we're updating settings to avoid loops
  const isUpdatingSettingsRef = useRef(false);

  // Sync darkMode with settings when they load
  useEffect(() => {
    if (settings && !updating && !isUpdatingSettingsRef.current) {
      // Only update if settings has a defined darkMode property
      if (typeof settings.darkMode === 'boolean') {
        logger.debug('Syncing dark mode from settings context:', { darkMode: settings.darkMode });
        
        // Compare with current value to avoid unnecessary updates
        if (darkMode !== settings.darkMode) {
          logger.debug('Updating dark mode from settings:', { 
            current: darkMode, 
            new: settings.darkMode 
          });
          setDarkMode(settings.darkMode);
          
          // Also update localStorage for faster loading on subsequent visits
          localStorage.setItem(DARK_MODE_STORAGE_KEY, settings.darkMode.toString());
        }
        
        // Mark as initialized
        if (!initialized) {
          setInitialized(true);
        }
      } else if (!initialized) {
        // If settings don't have darkMode property yet, we're using the default
        // Mark as initialized to prevent waiting indefinitely
        logger.debug('Settings loaded but no darkMode property, using current value');
        setInitialized(true);
      }
    }
  }, [settings, updating, darkMode, initialized]);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Also update localStorage when dark mode changes
    localStorage.setItem(DARK_MODE_STORAGE_KEY, darkMode.toString());
    
    logger.debug('Applied dark mode to document:', { darkMode });
  }, [darkMode]);

  // Toggle dark mode
  const toggleDarkMode = useCallback(async () => {
    logger.debug('Toggling dark mode, current state:', { darkMode });
    
    const newDarkMode = !darkMode;
    
    // Optimistically update the UI
    setUpdating(true); // Prevent settings from updating us while we're toggling
    setDarkMode(newDarkMode);
    
    try {
      // Update settings in the database through SettingsContext
      isUpdatingSettingsRef.current = true;
      await updateSettings({ darkMode: newDarkMode });
      logger.debug('Dark mode updated in settings:', { darkMode: newDarkMode });
    } catch (error) {
      logger.error('Failed to update dark mode in settings:', error);
      // Revert on error
      setDarkMode(darkMode);
    } finally {
      isUpdatingSettingsRef.current = false;
      setUpdating(false);
    }
  }, [darkMode, updateSettings]);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};